<template>
  <PageTemplate>
    <h1>Meeting Documents</h1>
    <fieldset class="fieldset">
      <v-input-wrap prefix="search">
        <v-input
          ref="meetingDocSearch"
          type="search"
          @search="performSearchAction"
          @input="performSearch"
          @keyup="performSearchAction"
        />
      </v-input-wrap>
    </fieldset>
    <v-card
      :title="getViewTitle()"
    >
      <v-table
        :table="recentDocumentTable"
        @downloadDoc="download"
      />
    </v-card>

    <v-card
      title="Documents"
      :padded="false"
    >
      <section class="file-system">
        <div
          class="file-system__col"
          :class="{'file-system__col--selected': selected.rootFolder}"
        >
          <template v-if="rootFolders && rootFolders.length > 0">
            <v-folder
              v-for="(rootFolder, index) in rootFolders"
              :key="index"
              :title="rootFolder.name"
              :subtitle="rootFolder.subtitle"
              :is-selected="rootFolder.id === selected.rootFolder"
              @click="loadSubFolder(rootFolder.id)"
            >
              <template
                v-if="isUserCommitteeAdmin"
                #menu
              >
                <v-dropdown-item @click="newFolderModal = rootFolder">
                  Create Sub Folder
                </v-dropdown-item>
                <v-dropdown-item @click="openAccessModal(rootFolder)">
                  Access
                </v-dropdown-item>
                <v-dropdown-item @click="deleteModal = rootFolder">
                  Delete Folder
                </v-dropdown-item>
              </template>
            </v-folder>
          </template>
          <div
            v-else
            class="file-system__message"
          >
            No folders available!
          </div>
          <div
            v-if="isUserCommitteeAdmin"
            class="file-system__actions"
          >
            <v-button
              v-if="isUserCommitteeAdmin"
              size="sm"
              @click="newFolderModal = true"
            >
              Create New Folder
            </v-button>
          </div>
        </div>
        <div
          v-if="selected.rootFolder"
          class="file-system__col"
          :class="{'file-system__col--selected': selected.subFolder}"
        >
          <div class="file-system__nav">
            <button
              class="file-system__back"
              @click="clearRootFolder"
            >
              <v-icon type="angle-left" />
            </button>
            <v-folder
              :title="rootFolders.find(folder => folder.id === selected.rootFolder).name"
              is-selected
            />
          </div>
          <template v-if="subFolders && subFolders.length > 0">
            <v-folder
              v-for="(subFolder, index) in subFolders"
              :key="index"
              :title="subFolder.name"
              :subtitle="subFolder.subtitle"
              :is-selected="subFolder.id === selected.subFolder"
              @click="loadDocuments(subFolder.id)"
            >
              <template
                v-if="isUserCommitteeAdmin"
                #menu
              >
                <v-dropdown-item @click="openAccessModal(subFolder)">
                  Access
                </v-dropdown-item>
                <v-dropdown-item @click="deleteModal = subFolder">
                  Delete Folder
                </v-dropdown-item>
              </template>
            </v-folder>
          </template>
          <div
            v-else
            class="file-system__message"
          >
            No folders available!
          </div>
        </div>
        <div
          v-show="selected.subFolder"
          class="file-system__col"
        >
          <div class="file-system__header file-system__header--dark">
            <div class="file-system__header-col">
              <v-checkbox
                id="file-system-all"
                class="file-system__all"
                :checked="checkDownloadAll"
                @change="selectAllAction"
              >
                Select all
              </v-checkbox>
            </div>
            <div class="file-system__header-col">
              <v-button
                size="sm"
                :is-disabled="!isDocumentIndexServiceAvailable"
                @click="$refs.uploadmodal.openModal()"
              >
                Upload Document
              </v-button>
            </div>
          </div>
          <div class="file-system__body">
            <div class="file-system__nav">
              <button
                class="file-system__back"
                @click="clearSubFolder"
              >
                <v-icon type="angle-left" />
              </button>
              <v-folder
                v-for="(rootFolder, index) in subFolders.filter(folder => folder.id === selected.subFolder)"
                :key="index"
                :title="rootFolder.name"
                :subtitle="rootFolder.subtitle"
                is-selected
              />
            </div>
            <template v-if="documents.length > 0">
              <v-document
                v-for="(document, index) in documents"
                :id="document.id"
                :key="index"
                class="file-system__document"
                :title="document.name"
                :link="'meetingdocuments/' + document.id"
                :checkbox="true"
                :select-action="selectUnselectDocument"
                :is-selected="document.isSelected"
                :can-delete="isUserCommitteeAdmin"
                :updated="formatDocumentDate(document.updated_at)"
                @click="download"
                @delete="deleteSingleDocumentConfirmModel(document)"
              />
            </template>
            <div
              v-else
              class="file-system__message"
            >
              No documents available!
            </div>
          </div>
          <v-button-group class="file-system__actions">
            <v-button
              :is-disabled="disableBulkAction"
              size="sm"
              :is-block="isUserCommitteeAdmin"
              @click="downloadSelected()"
            >
              Download Selected
            </v-button>
            <v-button
              v-if="isUserCommitteeAdmin"
              :is-disabled="disableBulkAction"
              size="sm"
              is-block
              @click="deleteSelected()"
            >
              Delete Selected
            </v-button>
          </v-button-group>
        </div>
      </section>
    </v-card>
    <v-uploadmodal
      ref="uploadmodal"
      :is-description-required="false"
      @upload="upload"
    />
    <v-modal
      v-if="newFolderModal"
      @close="closeModal"
    >
      <template #header>
        New Folder
      </template>
      <v-fieldset
        v-model="newFolderName"
        text="Folder Name"
        is-required
      />
      <template #footer>
        <v-button-group is-right>
          <v-button
            :is-disabled="!newFolderName"
            size="lg"
            has-min-width
            @click="createFolder(newFolderModal.id || null)"
          >
            Create
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeModal"
          >
            Cancel
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
    <v-modal
      v-if="deleteModal"
      @close="closeModal"
    >
      <template #header>
        {{ modalConfirmed ? "Success" : "Confirmation" }}
      </template>
      <p v-if="modalConfirmed">
        You have successfully deleted <strong>{{ deleteModal.name }}</strong> meeting folder.
      </p>
      <p v-else>
        Deleting <strong>{{ deleteModal.name }}</strong> meeting folder will delete all the sub folders
        and files within it. Are you sure you want to continue deleting
        this folder?
      </p>
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="!modalConfirmed"
            color="danger"
            size="lg"
            has-min-width
            @click="deleteFolder(deleteModal.id)"
          >
            Yes, delete
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeModal"
          >
            {{ modalConfirmed ? "OK, got it!" : "Cancel" }}
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
    <v-modal
      v-if="deleteDocumentModal"
      @close="closeModal"
    >
      <template #header>
        {{ modalConfirmed ? "Success" : "Confirmation" }}
      </template>
      <p v-if="modalConfirmed">
        You have successfully deleted <strong
          v-for="doc in deleteDocumentModal.docs"
          :key="doc.id"
          style="display:block;"
        >{{ doc.name }}</strong> meeting folder.
      </p>
      <p v-else>
        Deleting following documents:<br><strong
          v-for="doc in deleteDocumentModal.docs"
          :key="doc.id"
          style="display:block;"
        >{{ doc.name }}</strong>
        Are you sure want to continue to delete?
      </p>
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="!modalConfirmed"
            color="danger"
            size="lg"
            has-min-width
            @click="deleteDocument(deleteDocumentModal)"
          >
            Yes, delete
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeModal"
          >
            {{ modalConfirmed ? "OK, got it!" : "Cancel" }}
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
    <v-modal
      v-if="accessModal"
      @close="closeModal"
    >
      <template #header>
        {{ modalConfirmed ? "Success" : (selectedMembersConfirmed ? 'Confirmation' : 'Assign Access') }}
      </template>
      <template v-if="selectedMembersConfirmed">
        <p v-if="modalConfirmed">
          You have successfully assigned access to following
          committee members to <strong>{{ accessModal.name }}</strong> folder:
        </p>
        <p v-else>
          Are you sure you want to update access to the following
          committee members to <strong>{{ accessModal.name }}</strong> folder?
        </p>
        <v-list :items="selectedMembers" />
      </template>
      <template v-else>
        <p>Select from the list of committee members to assign access to <strong>{{ accessModal.name }}</strong> folder.</p>
        <v-checkbox-list
          id="member-list"
          v-model="selectedMembers"
          :groups="members"
          :selected-group="selectedMembers"
          @change="folderMemberChange()"
        />
      </template>
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="!selectedMembersConfirmed"
            :is-disabled="isContinueDisabled"
            size="lg"
            has-min-width
            @click="selectedMembersConfirmed = true"
          >
            Continue
          </v-button>
          <v-button
            v-if="!modalConfirmed && selectedMembersConfirmed"
            size="lg"
            has-min-width
            @click="assignAccess()"
          >
            Yes, Assign Access
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeModal"
          >
            {{ modalConfirmed ? "OK, got it!" : "Cancel" }}
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
  </PageTemplate>
</template>
<script>
  import Button from "@/atoms/Button";
  import Card from "@/molecules/Card";
  import Checkbox from "@/atoms/Checkbox";
  import Document from "@/molecules/Document";
  import Folder from "@/molecules/Folder";
  import PageTemplate from "@/templates/Page/index.vue";
  import Table from "@/molecules/Table";
  import UploadModal from "@/organisms/UploadModal";
  import InputWithIcon from "@/molecules/InputWithIcon";
  import ButtonGroup from "@/molecules/ButtonGroup";
  import Input from "@/atoms/Input";
  import DropdownItem from "@/atoms/DropdownItem";
  import Fieldset from "@/molecules/Fieldset";
  import Modal from "@/atoms/Modal";
  import Icon from "@/atoms/Icon";
  import CheckboxList from "@/molecules/CheckboxList";
  import List from "@/molecules/List";
  import {buildBreadCrumb} from "@/common/tableFunctions";
  import {mapState, mapActions} from 'vuex'
  import {columnFilter, columnSort} from "@/common/tableFunctions";
  import {checkIfCommitteeAdmin} from "@/common/switchCommittee";
  import {formatDate} from "../common/formatting";
  import routes from "@/common/routes";

  export default {
    name: "MeetingDocuments",
    components: {
      PageTemplate,
      "v-button": Button,
      "v-card": Card,
      "v-checkbox": Checkbox,
      "v-document": Document,
      "v-folder": Folder,
      "v-table": Table,
      "v-uploadmodal": UploadModal,
      "v-input": Input,
      "v-input-wrap": InputWithIcon,
      "v-dropdown-item": DropdownItem,
      "v-button-group": ButtonGroup,
      "v-modal": Modal,
      "v-fieldset": Fieldset,
      "v-checkbox-list": CheckboxList,
      "v-list": List,
      "v-icon": Icon,
    },
    data() {
      return {
        checkDownloadAll: false,
        isSearchMode: false,
        searchResults: [],
        searchKeyword: '',
        recentDocsBackup: [],
        disableBulkAction: true,
        isUserCommitteeAdmin: false,
        isDocumentIndexServiceAvailable: false,
        deleteModal: null,
        accessModal: null,
        deleteDocumentModal: null,
        modalConfirmed: false,
        newFolderModal: null,
        newFolderName: '',
        members: [],
        folderMembers: null,
        selectedMembers: [],
        selectedMembersConfirmed: false,
        isContinueDisabled: true,
        recentDocumentTable:{
          columns: [
            {
              label: 'Name',
              field: 'name',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Search',
                filterFn: columnFilter
              },
            },
            {
              label: 'Date Updated',
              field: 'dateUpdated',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Date',
                filterFn: columnFilter
              },
            },
            {
              label: 'Actions',
              field: 'actions',
              sortable: false,
              actions: 'download',
              thClass: 'shrink-cell',
              tdClass: 'no-wrap'
            },
          ],
          rows: [

          ],
          totalRecords: 0,
          perPage: [
            {
              label: "5",
              value: 5
            },
            {
              label: "10",
              value: 10
            }
          ]
        }
      };
    },
    computed: {
      ...mapState({
        recentDocumentTableRow: state => state.meetingDocuments.recentDocumentTableRow,
        rootFolders: state => state.meetingDocuments.rootFolders,
        subFolders: state => state.meetingDocuments.subFolders,
        documents: state => state.meetingDocuments.documents,
        selected: state => state.meetingDocuments.selected,
      }),
      selectedCommittee() {
        return this.$store.getters.getSelectedCommittee;
      },
      checkIfCommitteeAdmin,
    },
    watch: {
      selectedCommittee: {
        handler(val, oldVal) {
          this.switchCommittee(val);
        }
      },
      recentDocumentTableRow:{
        handler(val){
          this.recentDocumentTable.rows = val;
        }
      }
    },
    mounted() {
      this.updateBreadCrumb();
      if (this.selectedCommittee) {
        this.loadRecentDocuments(this.selectedCommittee);
        this.loadRootFolders(this.selectedCommittee);
        this.isUserCommitteeAdmin = this.checkIfCommitteeAdmin;
        if(this.isUserCommitteeAdmin) {
          this.loadCommitteeMembers();
        }
      }
      this.checkDocumentIndexServiceAvailable();
    },
    methods: {
      ...mapActions([
        'getRecentDocuments',
        'clearRootFolder',
        'clearSubFolder',
        'getRootFolders',
        'getSubFolders',
        'getDocuments',
        'uploadDocument',
        'downloadDocument',
        'downloadBulk',
        'searchDocuments',
        'checkDocumentIndexService',
        'deleteBulkDocuments',
        'deleteDocumentAction',
        'deleteFolderAction',
        'createFolderAction',
        'getCommitteeMembers',
        'getFolderMembers',
        'folderPermission',
      ]),
      updateBreadCrumb: function () {
        let breadcrumbs = [];
        breadcrumbs['Dashboard'] = routes.DASHBOARD;
        breadcrumbs['Meeting Documents'] = '';
        this.$store.dispatch('updateBreadCrumbAction', buildBreadCrumb(breadcrumbs));
      },
      switchCommittee: function (committeId) {
        this.loadRecentDocuments(committeId);
        this.loadRootFolders(committeId);
        this.searchKeyword = '';
        this.$refs['meetingDocSearch'].$el.value = '';
        this.isSearchMode = false;
        this.isUserCommitteeAdmin = this.checkIfCommitteeAdmin;
        if(this.isUserCommitteeAdmin) {
          this.loadCommitteeMembers();
        }
      },
      getViewTitle: function () {
        return this.isSearchMode ? "Search Results" : "Recently Updated Documents";
      },
      selectUnselectDocument(evt) {
        let res = true;
        this.documents.map(ele => {
          if (ele.id == evt.target.id) {
            ele.isSelected = evt.target.checked;
          }
          if (ele.isSelected) {
            res = false;
          }
        });
        this.disableBulkAction = res;
      },
      selectAllAction(evt) {
        this.checkDownloadAll = evt.target.checked;
        this.documents.map(ele => {
          ele.isSelected = evt.target.checked;
        });
        this.disableBulkAction = !evt.target.checked;
      },
      download(link) {
        this.$store.state.auth.isLoading = true;
        this.downloadDocument(link).then(res => {
          this.$notify({
              title: 'Success',
              text: res.message,
              type: 'success'
            });
        }).catch(err => {
          this.$notify({
                title: 'Error',
                text: err.message,
                type: 'error'
              });
        }).finally(() => {
            this.$store.state.auth.isLoading = false;
          });
      },
      downloadSelected() {
        this.disableBulkAction = true;
        let res = this.documents.filter(ele => ele.isSelected).map(ele => ele.id);
        this.$store.state.auth.isLoading = true;
        this.downloadBulk(res).then(res => {
          this.$notify({
              title: 'Success',
              text: res.message,
              type: 'success'
            });
        }).catch(err => {
          this.$notify({
                title: 'Error',
                text: err.message,
                type: 'error'
              });
        }).finally(() => {
          this.selectAllAction({target: {checked:false}});
            this.$store.state.auth.isLoading = false;
          });
      },
      deleteSingleDocumentConfirmModel(documents) {
        this.deleteDocumentModal = {
          docs: [documents],
          deleteType: 'SINGLE_DOC'
          }//{docs:[document.name]};
      },
      deleteDocument(deleteDocumentModal) {
        this.modalConfirmed = true;
        if(deleteDocumentModal.deleteType === 'SINGLE_DOC') {
          this.deleteSingleDocument(deleteDocumentModal.docs[0].id);
        }
        if(deleteDocumentModal.deleteType === 'MULTIPLE_DOC') {
          this.deleteBulk(deleteDocumentModal.docs);
        }
      },
      async deleteSingleDocument(docId) {
        await this.deleteDocumentAction({'docId':docId}).then(res => {
          this.$notify({
              title: 'Success',
              text: res.message,
              type: 'success'
            });
        }).catch(err => {
          this.$notify({
                title: 'Error',
                text: err.message,
                type: 'error'
              });
        });
        this.getDocuments(this.selected.subFolder);
        this.getRecentDocuments(this.selectedCommittee);
      },
      deleteSelected() {
        let res = this.documents.filter(ele => ele.isSelected);
         this.deleteDocumentModal = {
          docs: res,
          deleteType: 'MULTIPLE_DOC'
          }
      },
      async deleteBulk(docs) {
         let res = docs.map(ele => ele.id);
        await this.deleteBulkDocuments({'docIds':res,'committeeId':this.selectedCommittee}).then(res => {
          this.$notify({
              title: 'Success',
              text: res.message,
              type: 'success'
            });
        }).catch(err => {
          this.$notify({
                title: 'Error',
                text: err.message,
                type: 'error'
              });
            this.closeModal();
        });
        this.disableBulkAction = true;
        this.getDocuments(this.selected.subFolder);
        this.getRecentDocuments(this.selectedCommittee);
      },
      async upload(file, fileDescription) {
        await this.uploadDocument({
          'file': file,
          'folderId': this.selected.subFolder
        }).then(res => {
          this.$refs.uploadmodal.closeModal();
          this.$notify({
              title: 'Success',
              text: 'Document uploaded successfully!',
              type: 'success'
            });
        }).catch(err => {
                this.$notify({
                      title: 'Error',
                      text: err.message,
                      type: 'error'
                    });
                this.$refs.uploadmodal.closeModal();
              });;
        this.selectAllAction({target: {checked:false}});
        this.loadRecentDocuments(this.selectedCommittee);
        this.getDocuments(this.selected.subFolder);
      },
      loadRecentDocuments: function (committeId) {
        this.getRecentDocuments(committeId);
      },
      loadRootFolders: function (cmid) {
        this.checkDownloadAll = false;
        this.getRootFolders(cmid);
      },
      loadSubFolder(rootFolderId) {
        if (this.selected.rootFolder == rootFolderId)
          return;
        this.checkDownloadAll = false;
        this.getSubFolders(rootFolderId);
      },
      loadDocuments(subFolderId) {
        if (this.selected.subFolder == subFolderId)
          return;
        this.checkDownloadAll = false;
        this.getDocuments(subFolderId);
      },
      performSearch: function (evt) {
        this.searchKeyword = evt;
        if (evt.length === 0) {
          this.isSearchMode = false;
          this.$store.state.meetingDocuments.recentDocumentTableRow = this.recentDocsBackup;
        }
      },
      performSearchAction: function (evt) {
        this.searchKeyword = evt.target? evt.target.value: evt;
        if(this.searchKeyword == this.processedKey) {
          return;
        }
        this.processedKey = ''+this.searchKeyword;
        if (!this.isSearchMode) {
          this.recentDocsBackup = this.$store.state.meetingDocuments.recentDocumentTableRow;
        }
        this.isSearchMode = (this.searchKeyword.length !== 0);
        if (this.isSearchMode) {
          this.searchDocuments({'term': this.searchKeyword, 'cmid': this.selectedCommittee});
        } else {
          this.$store.state.meetingDocuments.recentDocumentTableRow = this.recentDocsBackup;
        }

      },
      checkDocumentIndexServiceAvailable: function () {
        this.checkDocumentIndexService().then(res => {
          this.isDocumentIndexServiceAvailable = res;
        }).catch(err => {
          this.isDocumentIndexServiceAvailable = false;
        });
      },
      createFolder(rootFolderID) {
        let folderName = this.newFolderName;
        this.createFolderAction({'folderName':folderName, 'parentFolderId':rootFolderID, 'committeeId':this.selectedCommittee}).then(res => {
                 if(rootFolderID) {
                   this.selected.rootFolder = rootFolderID;
                    this.getSubFolders(rootFolderID);
                 }else {
                   this.getRootFolders(this.selectedCommittee);
                 }

                this.$notify({
                    title: 'Success',
                    text: res.message,
                    type: 'success'
                  });
              }).catch(err => {
                this.$notify({
                      title: 'Error',
                      text: err.message,
                      type: 'error'
                    });
              });
        this.closeModal();
      },
      deleteFolder(folderId) {
        this.deleteFolderAction({'folderId':folderId}).then(res => {
                this.switchCommittee(this.selectedCommittee);
                this.$notify({
                    title: 'Success',
                    text: res.message,
                    type: 'success'
                  });
              }).catch(err => {
                this.$notify({
                      title: 'Error',
                      text: err.message,
                      type: 'error'
                    });
              });
        this.modalConfirmed = true;
      },
      assignAccess() {
        if (this.checkIfChangeInPermission()) {
          this.folderPermission({'folderId':this.accessModal.id, 'userIds':this.selectedUserIds(), 'committeeId':this.selectedCommittee}).then(res => {
            this.$notify({
                        title: 'Success',
                        text: res.message,
                        type: 'success'
                      });
                      this.modalConfirmed = true;
          }).catch(err => {
                    this.$notify({
                          title: 'Error',
                          text: err.message,
                          type: 'error'
                        });
                  });
        }else {
           this.modalConfirmed = true;
        }


      },
      checkIfChangeInPermission() {
        let a = this.selectedUserIds().sort();
        let b = this.folderMembers.directMembers.sort();
        let res = a.join(',') === b.join(',');
        return !res;
      },
      folderMemberChange() {
        this.isContinueDisabled = !this.checkIfChangeInPermission();
      },
      selectedUserIds() {
        let userIds = [];
        this.selectedMembers.forEach(ele => {
          // No need to update disabled items. otherway update only enabled ones.
          if(!this.folderMembers.indirectMembers.filter(e => e === ele.id).length > 0) {
            userIds.push(ele.id);
          }
        });
        return userIds;
      },
      loadCommitteeMembers() {
        this.getCommitteeMembers({'committeeId':this.selectedCommittee}).then(res => {
                this.members = res.data;
                this.members.map(ele => {
                  ele.selected = false;
                  ele.value = ele.first_name + ' '+ele.last_name + this.isYou(ele.id);
                  ele.isDisabled = false;
                });

              }).catch(err => {
                this.$notify({
                      title: 'Error',
                      text: err.message,
                      type: 'error'
                    });
              });
      },
      closeModal() {
        this.newFolderModal = null;
        this.newFolderName = null;
        this.deleteModal = null;
        this.accessModal = null;
        this.deleteDocumentModal = null;
        this.modalConfirmed = false;
        this.selectedMembers = [];
        this.selectedMembersConfirmed = false;
      },
      openAccessModal(folder) {
       this.getFolderMembers({'committeeId':this.selectedCommittee, 'folderId':folder.id}).then(res => {
         this.folderMembers = res.data;
         this.folderMembers.allMembers = [...new Set([...this.folderMembers.directMembers, ...this.folderMembers.indirectMembers])];

         this.selectedMembers = [];
         this.folderMembers.allMembers.forEach(ele => {
           this.selectedMembers.push({'id':ele, 'value':this.getValueByMemberId(ele)});

         });
        //  disable members with indirect access.
         this.members.forEach(ele => {
           ele.isDisabled = false;
           if(this.folderMembers.indirectMembers.includes(ele.id)) {
             ele.isDisabled = true;
           }
         });
         this.accessModal = folder;
       }).catch(err=>{
         console.error('Error :'+err);
       });
      },
      getValueByMemberId(id) {
        let ele = this.members.find(ele => ele.id === id);
        return ele?ele.value:'Unknown';
      },
      isYou(id) {
        return (id === this.$store.state.auth.user.id)?' (You)':'';
      },
      formatDocumentDate(updated){
        return formatDate(updated);
      }
    },
  }
</script>
